/*
 * Seepje cart drawer. Rewritten from Dawn.
 *
 * Loaded globally from sections/header.liquid, AFTER cart.js (CartDrawerItems extends CartItems).
 *
 * WHY THE DAWN NAMES ARE KEPT: product-form.js:11, quick-add.js:11 and roughly a dozen sites in
 * cart.js are already written against `<cart-drawer>` / `<cart-drawer-items>` and the `CartDrawer-*`
 * DOM ids. Honouring that contract means those three shared files need zero edits — including the
 * GA4 dataLayer push in product-form.js. Only the markup and CSS are ours.
 *
 * BEWARE — getSectionsToRender() has TWO INCOMPATIBLE SHAPES in this codebase, and this is the thing
 * a future maintainer will get wrong:
 *
 *   CartDrawer.getSectionsToRender()       consumed by product-form.js:35-38 and renderContents()
 *                                          -> `id` IS THE SECTION ID (it goes into the `sections=`
 *                                             param and keys parsedState.sections)
 *   CartDrawerItems.getSectionsToRender()  consumed by cart.js:157-194
 *                                          -> `id` is a DOM id, `section` is the section id
 *
 * Both below are correct for their own consumer. Do not "unify" them.
 */

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (event) => event.code === 'Escape' && this.close());

    /*
     * ONE delegated listener for every close affordance. The close button and the "verder winkelen"
     * link live inside .cart-drawer__footer, which is replaced on every cart mutation — per-element
     * binding would silently stop working (and Dawn's cart-drawer.js:83 re-binds the overlay on
     * every render, leaking a listener each time).
     */
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-cart-drawer-close]')) {
        event.preventDefault();
        this.close();
        return;
      }

      /*
       * Click outside the panel closes. Tested as "not inside #CartDrawer" rather than "is the
       * overlay", so it holds regardless of the overlay's geometry — or if the overlay element is
       * ever missing. <cart-drawer> is inset:0, so any click that reaches it and misses the panel is
       * by definition on the dimmed area.
       *
       * Only while open: the element still spans the viewport when closed (visibility:hidden +
       * pointer-events:none), and this guard means a stray event can never call close() on an
       * already-closed drawer and steal focus back from the page.
       */
      if (this.classList.contains('active') && !event.target.closest('#CartDrawer')) {
        this.close();
      }
    });

    this.bindHeaderIcon();
  }

  bindHeaderIcon() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    // Null-guard: sections/header.liquid:216 suppresses the icon for the Soaply shop when
    // settings.hide_cart is on. Dawn's version throws here.
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    // Both SPACE and ENTER. Dawn only handles SPACE, so keyboard-Enter navigates to /cart instead
    // of opening the drawer. The href stays as the no-JS and middle-click fallback.
    cartLink.addEventListener('keydown', (event) => {
      const key = (event.code || '').toUpperCase();
      if (key === 'SPACE' || key === 'ENTER' || key === 'NUMPADENTER') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    // The class flip has to land in a later frame than any preceding innerHTML write, or the browser
    // coalesces them and the panel appears without sliding.
    requestAnimationFrame(() => {
      this.classList.add('animate', 'active');
    });

    this.trapDrawerFocus();
    document.body.classList.add('overflow-hidden');

    this.dispatchEvent(new CustomEvent('cartDrawer:open', { bubbles: true }));
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');

    this.dispatchEvent(new CustomEvent('cartDrawer:close', { bubbles: true }));
  }

  /*
   * MUST be re-called after every re-render. trapFocus() snapshots the focusable elements once (see
   * getFocusableElements in global.js), so without this Tab escapes the drawer after the first
   * quantity change. Dawn only re-traps on the quantity path, by accident, at cart.js:209-215.
   */
  trapDrawerFocus() {
    const panel = document.getElementById('CartDrawer');
    if (!panel) return;

    const container = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') || panel : panel;
    const focusElement = container.querySelector('.cart-drawer__close') || container.querySelector('a, button') || panel;
    trapFocus(container, focusElement);
  }

  /*
   * Called by product-form.js:88 with the /cart/add.js response.
   *
   * Deliberately surgical rather than replacing the whole panel: <cart-progress> must survive so its
   * CSS width transition can play and its aria-live node stays stable, and <cart-upsell> must
   * survive so the element (and its module-level cache) is not destroyed. See the boundary note in
   * snippets/cart-drawer.liquid.
   */
  renderContents(parsedState) {
    const html = parsedState.sections && parsedState.sections['cart-drawer'];
    if (html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      this.applyDrawerDocument(doc, { seedProductId: parsedState.product_id });
    }

    const bubbleHTML = parsedState.sections && parsedState.sections['cart-icon-bubble'];
    if (bubbleHTML) this.renderCartIconBubble(bubbleHTML);

    this.open();
  }

  /*
   * The single place that maps a rendered cart-drawer section onto the live DOM. Everything that can
   * change on a cart mutation flows through here, so there is exactly one refresh path to reason
   * about. Called from renderContents(), from CartDrawerItems.onCartUpdate(), and from the gift
   * reconciler in cart-drawer-features.js.
   */
  applyDrawerDocument(doc, options = {}) {
    ['.js-contents', '.cart-drawer__footer'].forEach((selector) => {
      const target = this.querySelector(selector);
      const incoming = doc.querySelector(selector);
      if (target && incoming) target.innerHTML = incoming.innerHTML;
    });

    this.patchAncillary(doc, options);
  }

  /*
   * Everything outside the two re-render selectors: the state data-* attributes, the progress bars,
   * the gift state island, the upsell, and the focus trap.
   *
   * Split out from applyDrawerDocument() because cart.js#updateQuantity does its own swapping of
   * `.js-contents` and `.cart-drawer__footer` (via CartDrawerItems.getSectionsToRender) and then
   * publishes cartUpdate. On that path we must patch ONLY the ancillary parts — re-writing the two
   * innerHTMLs would clobber the focus cart.js just restored, for no benefit.
   */
  patchAncillary(doc, options = {}) {
    const source = doc.querySelector('cart-drawer');

    // The state channel: these attributes live OUTSIDE every re-render selector, so they survive a
    // mutation and are the only thing JS reads to decide what to do.
    if (source) {
      Array.from(source.attributes).forEach((attr) => {
        if (attr.name.startsWith('data-')) this.setAttribute(attr.name, attr.value);
      });
      this.classList.toggle('is-empty', source.classList.contains('is-empty'));

      const items = this.querySelector('cart-drawer-items');
      if (items) items.classList.toggle('is-empty', source.classList.contains('is-empty'));
    }

    // Patched in place, never innerHTML-swapped — see the boundary note in snippets/cart-drawer.liquid.
    if (window.CartProgress) window.CartProgress.patchFrom(doc);
    if (window.GiftReconciler) window.GiftReconciler.ingest(doc);
    if (window.CartUpsell) window.CartUpsell.sync(doc, options);

    this.trapDrawerFocus();
  }

  renderCartIconBubble(html) {
    const bubble = document.getElementById('cart-icon-bubble');
    if (!bubble) return;
    const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
    if (parsed) bubble.innerHTML = parsed.innerHTML;
  }

  /* `id` here is a SECTION id — see the header comment. */
  getSectionsToRender() {
    return [{ id: 'cart-drawer', selector: '#CartDrawer' }, { id: 'cart-icon-bubble' }];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  /*
   * `id` is a DOM id and `section` is the section id here — the cart.js shape.
   *
   * Narrowed to the two selectors that actually depend on the cart. Dawn replaced `.drawer__inner`
   * wholesale, which would destroy <cart-progress> (killing the width transition and the aria-live
   * region) and <cart-upsell> (forcing a refetch) on every single quantity change.
   *
   * Repeating 'cart-drawer' in the sections param is harmless — Shopify dedupes it, and the base
   * class already repeats main-cart-footer at cart.js:100-108.
   */
  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.js-contents' },
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.cart-drawer__footer' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }

  /*
   * Narrows the inherited subscription. CartItems.connectedCallback only skips its own
   * `cart-items` source, which means the drawer would issue a redundant
   * `?section_id=cart-drawer` GET for two sources it has already fully handled:
   *
   *   'product-form' — CartDrawer.renderContents() applies the sections from the add response.
   *   'free-gift'    — GiftReconciler.render() applies the sections from the mutation response.
   *
   * <cart-items> on the /cart page keeps its own unnarrowed subscription, so the page's cart table
   * still refreshes for both of those — which it must, since a gift add has to become visible there.
   */
  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items' || event.source === 'product-form' || event.source === 'free-gift') {
        return;
      }
      this.onCartUpdate();
    });
  }

  /*
   * MUST be overridden. cart.js:50-67 branches on tagName === 'CART-DRAWER-ITEMS' and then
   * replaceWith()s `cart-drawer-items` — swapping out the very element running the code, destroying
   * this instance mid-flight. Delegating to the drawer gives one refresh path and keeps the element
   * alive. (This is also why all feature state in cart-drawer-features.js is module-level rather
   * than stored on an element instance.)
   *
   * This path is the cross-component one: a mutation that came from the PDP or the /cart page.
   */
  onCartUpdate() {
    const drawer = this.closest('cart-drawer');
    if (!drawer) return Promise.resolve();

    return fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        const doc = new DOMParser().parseFromString(responseText, 'text/html');
        drawer.applyDrawerDocument(doc);
      })
      .catch((e) => {
        console.error('[cart-drawer] refresh failed', e);
      });
  }

  /*
   * Keep the loading state inside the drawer. The inherited version reaches for
   * `#main-cart-items || #CartDrawer-CartItems`, so on the /cart page it would grey out the page's
   * own cart table whenever a drawer line changed.
   */
  enableLoading(line) {
    const container = document.getElementById('CartDrawer-CartItems');
    if (container) container.classList.add('cart__items--disabled');

    this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`).forEach((spinner) =>
      spinner.classList.remove('hidden')
    );

    if (document.activeElement) document.activeElement.blur();
    if (this.lineItemStatusElement) this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const container = document.getElementById('CartDrawer-CartItems');
    if (container) container.classList.remove('cart__items--disabled');

    this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`).forEach((spinner) =>
      spinner.classList.add('hidden')
    );
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
